import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { Company } from '../types'
import { BankDetailsResponse } from '../types/BankDetails'

/**
 * Redux slice для управления состоянием компании
 * Хранит информацию о текущей компании пользователя и её банковских реквизитах
 */

export type CompanyState = {
  // Информация о компании
  company: Company | null
  // Список банковских реквизитов
  bankDetails: BankDetailsResponse[]
  // ID выбранного по умолчанию реквизита
  defaultBankDetailsId: number | null
  // Флаги загрузки и ошибок
  isLoading: boolean
  error: string | null
}

const initialState: CompanyState = {
  company: null,
  bankDetails: [],
  defaultBankDetailsId: null,
  isLoading: false,
  error: null,
}

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    // Установить информацию о компании
    setCompany: (state, action: PayloadAction<Company | null>) => {
      state.company = action.payload
    },

    // Установить список банковских реквизитов
    setBankDetails: (state, action: PayloadAction<BankDetailsResponse[]>) => {
      state.bankDetails = action.payload
      // Найти реквизит по умолчанию
      const defaultBankDetails = action.payload.find(bd => bd.isDefault)
      state.defaultBankDetailsId = defaultBankDetails?.id || null
    },

    // Добавить новый банковский реквизит
    addBankDetails: (state, action: PayloadAction<BankDetailsResponse>) => {
      state.bankDetails.push(action.payload)
      // Если это первый реквизит или он отмечен как default
      if (action.payload.isDefault || state.bankDetails.length === 1) {
        state.defaultBankDetailsId = action.payload.id
      }
    },

    // Обновить банковский реквизит
    updateBankDetails: (state, action: PayloadAction<BankDetailsResponse>) => {
      const index = state.bankDetails.findIndex(
        bd => bd.id === action.payload.id,
      )
      if (index !== -1) {
        state.bankDetails[index] = action.payload
        // Если обновленный реквизит стал default
        if (action.payload.isDefault) {
          state.defaultBankDetailsId = action.payload.id
        }
      }
    },

    // Удалить банковский реквизит
    removeBankDetails: (state, action: PayloadAction<number>) => {
      state.bankDetails = state.bankDetails.filter(
        bd => bd.id !== action.payload,
      )
      // Если удален реквизит по умолчанию, выбрать первый из оставшихся
      if (state.defaultBankDetailsId === action.payload) {
        state.defaultBankDetailsId =
          state.bankDetails.length > 0 ? state.bankDetails[0].id : null
      }
    },

    // Установить реквизит по умолчанию
    setDefaultBankDetails: (state, action: PayloadAction<number>) => {
      state.defaultBankDetailsId = action.payload
      // Обновить флаг isDefault у всех реквизитов
      state.bankDetails = state.bankDetails.map(bd => ({
        ...bd,
        isDefault: bd.id === action.payload,
      }))
    },

    // Установить статус загрузки
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Установить ошибку
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    // Очистить состояние компании (при выходе пользователя)
    clearCompanyState: () => initialState,
  },
})

export const {
  setCompany,
  setBankDetails,
  addBankDetails,
  updateBankDetails,
  removeBankDetails,
  setDefaultBankDetails,
  setLoading,
  setError,
  clearCompanyState,
} = companySlice.actions

export const companyReducer = companySlice.reducer
